import React from 'react';
import { css } from '@emotion/react';

import { OverlayComponentProps } from '$/components/Interactions/InteractionInfo';
import { OverlayInteractionMode } from '$/components/Interactions/types';
import { usePropertyFilledLink } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { FormOptionsContext } from '$/components/ui/Form';
import { WebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { useOverlayDimensions } from '$/hooks/useOverlayDimensions';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { OverlayAnimation } from './optionsAnimation';
import { getAnimationProps } from './getAnimationProps';

export const reverseDirection = (direction: string): string => {
  switch (direction) {
    case 'Left':
      return 'Right';
    case 'Right':
      return 'Left';
    case 'Down':
      return 'Up';
    case 'Up':
      return 'Down';
    default:
      return direction;
  }
};

const ImageOverlay: React.FC<OverlayComponentProps> = ({
  interaction,
  overlayMode,
  settings,
}): JSX.Element | null => {
  const { onSubmit } = React.useContext(FormOptionsContext);
  settings = settings ?? interaction.details!.overlaySettings!;

  const isEdit = overlayMode === OverlayInteractionMode.Edit;
  const inCard = overlayMode === OverlayInteractionMode.InCard;
  const isLive = overlayMode === OverlayInteractionMode.Live;
  const isClickable = !isEdit && interaction.fragment.details?.link;

  const { rooms, playbackPosition } = React.useContext(WebinarPlayerContext);
  const dimensions = useOverlayDimensions(
    interaction,
    settings ?? interaction.details?.overlaySettings
  );

  const detailLink = usePropertyFilledLink(interaction.fragment.details?.link);

  const appearAt = interaction.appearAt();
  const endsAt = interaction.endsAtPlaybackPosition;
  const shouldShow = playbackPosition >= appearAt && playbackPosition < endsAt - 1;
  const imageUrl = usePropertyFilledLink(settings.imageMediaRef?.imageUrl ?? '');
  const [imageReady, setImageReady] = React.useState(false);
  React.useEffect(() => {
    // Load image before we render
    if (!imageUrl) {
      return;
    }

    const img = new Image();
    img.onload = (): void => {
      setImageReady(true);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  const animations = React.useMemo((): AnimationProps => {
    if (!settings?.animation || inCard) return {};
    return getAnimationProps(settings.animation as OverlayAnimation);
  }, [inCard, settings.animation]);

  if (!settings || !imageReady) {
    return null;
  }

  const backgroundColor = settings.background ?? '#00000000';

  // Grow zIndex based on appearAt
  const total = rooms.domain[1] - rooms.domain[0];
  const zIndex = Math.floor(100 + ((appearAt - rooms.domain[0]) / total) * 10000);

  const onClick = (): void => {
    if (detailLink) {
      window.open(detailLink, '_blank', 'noopener');
    }
    onSubmit();
  };

  return (
    <AnimatePresence
      key={settings.animation === 'None' ? 'sync' : 'wait'}
      mode={settings.animation === 'None' ? 'sync' : 'wait'}
    >
      {shouldShow && (
        <motion.div
          key={settings.animation}
          onClick={isClickable ? onClick : undefined}
          className={`
            ImageOverlay-${interaction.shortId}
            inline-block overflow-hidden
            ${isClickable && 'cursor-pointer'}
            ${isLive && 'absolute'}
            ${isEdit && 'cursor-move'}
          `}
          css={css`
            background: ${settings.background};
            border-radius: ${inCard ? 0 : settings.borderRadius}px;
            padding: ${!inCard && backgroundColor !== '#00000000'
              ? `${dimensions.paddingYpx}px ${dimensions.paddingXpx}px`
              : 0};
            z-index: ${zIndex};
            top: ${dimensions.y}px;
            left: ${dimensions.x}px;
            width: ${isEdit ? '100%' : `${dimensions.width}px`};
            max-width: ${dimensions.playerWidth}px;
            max-height: ${dimensions.playerHeight}px;
          `}
          {...animations}
        >
          <img src={imageUrl} alt='' className='w-full h-auto pointer-events-none' />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageOverlay;
